import React from 'react';

const FIELD_ALIASES = {
  level: ['level', 'escalation_level', 'sequence', 'order', 'priority', 'sl_no'],
  name: ['name', 'contact_name', 'person_name', 'escalation_person'],
  designation: ['designation', 'role', 'title'],
  phone: ['mobile_number', 'phone', 'mobile', 'contact', 'contact_no', 'contact_number', 'phone_number'],
  email: ['email_id', 'email', 'mail'],
  // department: ['department', 'team'],
  notes: ['notes', 'remark', 'remarks']
};

const isPresent = (value) => value !== undefined && value !== null && String(value).trim() !== '';

const pickFirst = (source, keys = []) => {
  for (const key of keys) {
    if (isPresent(source?.[key])) {
      return source[key];
    }
  }
  return undefined;
};

const normalizeLevel = (contact, fallbackIndex) => {
  const parsedLevel = Number(pickFirst(contact, FIELD_ALIASES.level));
  return Number.isFinite(parsedLevel) ? parsedLevel : fallbackIndex + 1;
};

const normalizeContact = (contact, index) => ({
  level: normalizeLevel(contact, index),
  name: pickFirst(contact, FIELD_ALIASES.name) || 'Unnamed Contact',
  designation: pickFirst(contact, FIELD_ALIASES.designation),
  phone: pickFirst(contact, FIELD_ALIASES.phone),
  email: pickFirst(contact, FIELD_ALIASES.email),
  // department: pickFirst(contact, FIELD_ALIASES.department),
  notes: pickFirst(contact, FIELD_ALIASES.notes)
});

const EscalationMatrix = ({
  contacts = [],
  title = 'Escalation Matrix',
  description = 'Follow the contact hierarchy below to escalate unresolved issues.'
}) => {
  const normalizedContacts = [...contacts]
    .map((contact, index) => normalizeContact(contact, index))
    .sort((a, b) => a.level - b.level);

  const panelStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '14px',
    padding: '20px'
  };

  const headerStyle = {
    marginBottom: '20px'
  };

  const titleStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: 'var(--text-primary)',
    marginBottom: '8px'
  };

  const descriptionStyle = {
    color: 'var(--text-secondary)',
    fontSize: '14px',
    lineHeight: 1.5,
    margin: 0
  };

  const emptyStateStyle = {
    background: 'var(--background)',
    border: '1px dashed var(--border)',
    borderRadius: '12px',
    padding: '28px',
    textAlign: 'center',
    color: 'var(--text-secondary)'
  };

  const chainStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  };

  const rowStyle = {
    display: 'block'
  };

  const cardStyle = {
    background: 'var(--background)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '14px 16px'
  };

  const topRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '10px'
  };

  const nameStyle = {
    margin: 0,
    fontSize: '16px',
    color: 'var(--text-primary)',
    fontWeight: '700'
  };

  const designationStyle = {
    margin: '4px 0 0',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    fontWeight: '600'
  };

  // const departmentStyle = {
  //   display: 'inline-block',
  //   border: '1px solid var(--border)',
  //   borderRadius: '999px',
  //   padding: '4px 10px',
  //   fontSize: '12px',
  //   color: 'var(--text-secondary)',
  //   background: 'var(--surface)'
  // };

  const detailsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '8px 14px'
  };

  const detailItemStyle = {
    fontSize: '13px',
    color: 'var(--text-secondary)',
    lineHeight: 1.5
  };

  const detailLabelStyle = {
    color: 'var(--text-primary)',
    fontWeight: '600',
    marginRight: '6px'
  };

  const connectorStyle = {
    display: 'flex',
    justifyContent: 'center',
    margin: '2px 0 0'
  };

  const connectorLineStyle = {
    width: '2px',
    height: '24px',
    background: 'var(--border)',
    position: 'relative'
  };

  const connectorArrowStyle = {
    position: 'absolute',
    bottom: '-6px',
    left: '-4px',
    width: 0,
    height: 0,
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent',
    borderTop: '7px solid var(--border)'
  };

  return (
    <section style={panelStyle}>
      <header style={headerStyle}>
        <h2 style={titleStyle}>{title}</h2>
        <p style={descriptionStyle}>{description}</p>
      </header>

      {normalizedContacts.length === 0 ? (
        <div style={emptyStateStyle}>No escalation contacts configured.</div>
      ) : (
        <div style={chainStyle}>
          {normalizedContacts.map((contact, index) => {
            const isLast = index === normalizedContacts.length - 1;
            return (
              <div key={`${contact.level}-${contact.name}-${index}`}>
                <div style={rowStyle}>
                  <div style={cardStyle}>
                    <div style={topRowStyle}>
                      <div>
                        <h3 style={nameStyle}>{contact.name}</h3>
                        {contact.designation && <p style={designationStyle}>{contact.designation}</p>}
                      </div>
                      {/* {contact.department && <span style={departmentStyle}>{contact.department}</span>} */}
                    </div>

                    <div style={detailsGridStyle}>
                      {contact.phone && (
                        <div style={detailItemStyle}>
                          <span style={detailLabelStyle}>Phone:</span>
                          <span>{contact.phone}</span>
                        </div>
                      )}
                      {contact.email && (
                        <div style={detailItemStyle}>
                          <span style={detailLabelStyle}>Email:</span>
                          <span>{contact.email}</span>
                        </div>
                      )}
                      {contact.notes && (
                        <div style={detailItemStyle}>
                          <span style={detailLabelStyle}>Notes:</span>
                          <span>{contact.notes}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {!isLast && (
                  <div style={connectorStyle}>
                    <div style={connectorLineStyle}>
                      <span style={connectorArrowStyle} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default EscalationMatrix;
